import { Component, Injector } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms'; 
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    ReactiveFormsModule, 
    CommonModule,
    RouterModule
  ],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9) translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.9) translateY(-20px)' }))
      ])
    ])
  ]
})
export class SignupComponent {
  signupForm: FormGroup;
  errorMessage: string | null = null;
  showResendEmailOption: boolean = false;
  userEmail: string = '';
  isResendingEmail: boolean = false;
  formattedErrorMessage: string = '';
  isLoading: boolean = false;
  focusedField: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private Authservice: AuthService,
    private router: Router,
    private cookieService: CookieService,
    private injector: Injector
  ) {
    this.signupForm = this._formBuilder.group({
      fullname: ['', Validators.required],
      username: ['', [Validators.required, Validators.minLength(3), this.usernameValidator]],
      email: ['', [Validators.required, Validators.email]],
      verify_email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{8,15}$')]],
      password: ['', [Validators.required, Validators.minLength(6), this.passwordStrengthValidator]],
      confirmPassword: ['', Validators.required],
      security_question: ['', Validators.required],
      security_answer: ['', Validators.required],
    }, { 
      validators: [this.passwordsMatchValidator, this.emailsMatchValidator] 
    });
  }

  /**
   * Calcule la force du mot de passe (0-100)
   */
  get passwordStrength(): number {
    const password = this.signupForm.get('password')?.value || '';
    if (!password) return 0;
    
    let strength = 0;
    
    // Longueur
    if (password.length >= 6) strength += 20;
    if (password.length >= 8) strength += 10;
    if (password.length >= 12) strength += 10;
    
    // Complexité
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
    
    return Math.min(strength, 100);
  }

  /**
   * S'inscrire avec CodinCloud SSO (Authentik) — flow SERVER-SIDE.
   *
   * Délègue 100% au backend (voir login.component.ts pour la doc complète).
   * Si le user n'existe pas, le backend ``/authentications/sso/authentik``
   * le crée automatiquement avec ``complete_signup=false`` ; le composant
   * ``AuthLandingComponent`` route alors vers ``/CompleteSignUp``.
   */
  signupWithCodinCloud(): void {
    const configService = this.injector.get(ConfigService);
    const apiUrl = configService.apiUrl?.replace(/\/+$/, '') || '';
    const apiRoot = apiUrl.replace(/\/authentications$/, '');
    const returnTo = encodeURIComponent('/CompleteSignUp');
    window.location.href = `${apiRoot}/auth/sso/start?return_to=${returnTo}`;
  }

  /**
   * Démarre le flow OAuth login (Google/Facebook) côté backend.
   *
   * Top-level redirect vers ``/auth/oauth/{provider}/start`` : si le compte
   * n'existe pas, le backend le crée (rôle vendor, ``vendor_status=incomplete``)
   * puis redirige vers ``/CompleteSignUp`` où le wizard reprend l'onboarding.
   */
  private startOAuth(provider: 'google' | 'facebook'): void {
    const configService = this.injector.get(ConfigService);
    const apiUrl = configService.apiUrl?.replace(/\/+$/, '') || '';
    const apiRoot = apiUrl.replace(/\/authentications$/, '');
    const returnTo = encodeURIComponent('/CompleteSignUp');
    window.location.href = `${apiRoot}/auth/oauth/${provider}/start?return_to=${returnTo}`;
  }

  /**
   * S'inscrire avec Facebook (OAuth login).
   */
  signupWithFacebook(): void {
    this.startOAuth('facebook');
  }

  /**
   * S'inscrire avec Google (OAuth login).
   */
  signupWithGoogle(): void {
    this.startOAuth('google');
  }

  /**
   * Ferme le modal d'erreur si on clique sur l'overlay
   */
  closeErrorModal(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('error-modal-overlay')) {
      this.errorMessage = null;
      this.showResendEmailOption = false;
    }
  }

  // Validateurs
  passwordsMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  emailsMatchValidator(group: FormGroup) {
    const email = group.get('email')?.value;
    const verifyEmail = group.get('verify_email')?.value;
    return email === verifyEmail ? null : { emailMismatch: true };
  }

  passwordStrengthValidator(control: any) {
    const password = control.value;
    if (!password) return null;
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    if (hasUpperCase && hasNumber) {
      return null;
    }
    
    return { passwordStrength: true };
  }

  usernameValidator(control: any) {
    const username = control.value;
    if (!username) return null;
    
    if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      return { usernameFormat: true };
    }
    
    if (/^[._-]|[._-]$/.test(username)) {
      return { usernameStartEnd: true };
    }
    
    return null;
  }

  submitSignup() {
    if (this.signupForm.valid) {
      this.isLoading = true;
      const formValues = this.signupForm.value;

      const questions: { question: string; answer: string }[] = [
        {
          question: formValues.security_question,
          answer: formValues.security_answer
        }
      ];

      // Attribution first-touch capturée par /_a/loader.js (utm_*, fbclid,
      // landing, referrer) : transmise au backend qui crée le lead CRM Odoo
      // attribué + l'événement CAPI. Fail-soft si stockage indisponible.
      let attribution: Record<string, string> | null = null;
      try {
        const raw = localStorage.getItem('vd-attrib');
        attribution = raw ? JSON.parse(raw) : null;
      } catch {
        attribution = null;
      }

      const userData = {
        fullname: formValues.fullname,
        username: formValues.username,
        phone: formValues.phone,
        email: formValues.email,
        verify_email: formValues.verify_email,
        password: formValues.password,
        confirm_password: formValues.confirmPassword,
        security_questions: questions,
        attribution,
      };

      this.Authservice.registeruser(userData).subscribe({
        next: (response) => {
          this.isLoading = false;
          console.log('Inscription réussie', response);
          
          if (response.email_sent === false) {
            this.errorMessage = response.message || 'Compte créé, mais l\'email de vérification n\'a pas pu être envoyé.';
            this.updateFormattedErrorMessage();
            this.showResendEmailOption = true;
            this.userEmail = response.email;
          } else {
            if (response.token) {
              this.cookieService.set('authToken', response.token, { secure: true, sameSite: 'Strict' });
            }
            this.router.navigate(['/loading']);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.handleSignupError(error);
          
          // Animation shake
          const container = document.querySelector('.signup-container');
          if (container) {
            container.classList.add('shake');
            setTimeout(() => container.classList.remove('shake'), 500);
          }
        }
      });
    }
  }

  private handleSignupError(error: any) {
    console.error('Erreur lors de l\'inscription', error);
    
    if (error.status === 0) {
      this.errorMessage = '❌ Impossible de contacter le serveur. Vérifiez votre connexion.';
      this.updateFormattedErrorMessage();
      return;
    }
    
    if (error.status === 422 && error.error?.detail && Array.isArray(error.error.detail)) {
      const validationErrors = error.error.detail;
      const userFriendlyMessages: string[] = [];
      
      for (const validationError of validationErrors) {
        const field = validationError.loc?.[1] || 'Unknown field';
        const message = validationError.msg || 'Invalid value';
        
        switch (field) {
          case 'username':
            if (message.includes('alphanumeric')) {
              userFriendlyMessages.push('❌ Le nom d\'utilisateur ne peut contenir que des lettres, chiffres, points, tirets et underscores');
            } else if (message.includes('3 characters')) {
              userFriendlyMessages.push('❌ Le nom d\'utilisateur doit contenir au moins 3 caractères');
            } else if (message.includes('start or end')) {
              userFriendlyMessages.push('❌ Le nom d\'utilisateur ne peut pas commencer ou finir par un point, tiret ou underscore');
            } else if (message.includes('cannot be empty')) {
              userFriendlyMessages.push('❌ Le nom d\'utilisateur ne peut pas être vide');
            } else {
              userFriendlyMessages.push(`❌ Nom d'utilisateur : ${message}`);
            }
            break;
          case 'email':
            userFriendlyMessages.push('❌ Format d\'email invalide');
            break;
          case 'password':
            userFriendlyMessages.push('❌ Le mot de passe doit contenir au moins 6 caractères avec une majuscule et un chiffre');
            break;
          case 'phone':
            userFriendlyMessages.push('❌ Le numéro de téléphone n\'est pas valide');
            break;
          case 'fullname':
            userFriendlyMessages.push('❌ Le nom complet n\'est pas valide');
            break;
          default:
            userFriendlyMessages.push(`❌ ${field}: ${message}`);
        }
      }
      
      this.errorMessage = userFriendlyMessages.join('\n');
      this.updateFormattedErrorMessage();
    } 
    else if (error.error?.detail === 'Email is already registered') {
      this.errorMessage = '❌ Cet e-mail est déjà utilisé. Veuillez en essayer un autre.';
      this.updateFormattedErrorMessage();
    } 
    else if (typeof error.error?.detail === 'string') {
      this.errorMessage = `❌ ${error.error.detail}`;
      this.updateFormattedErrorMessage();
    }
    else {
      this.errorMessage = '❌ Une erreur est survenue. Veuillez réessayer.';
      this.updateFormattedErrorMessage();
    }
  }

  private updateFormattedErrorMessage(): void {
    this.formattedErrorMessage = this.errorMessage ? this.errorMessage.replace(/\n/g, '<br>') : '';
  }

  resendVerificationEmail() {
    if (!this.userEmail || this.isResendingEmail) return;
    
    this.isResendingEmail = true;
    this.errorMessage = null;
    this.updateFormattedErrorMessage();
    
    this.Authservice.resendVerificationEmail(this.userEmail).subscribe({
      next: (response) => {
        this.isResendingEmail = false;
        if (response.email_sent) {
          this.errorMessage = '✅ Email de vérification renvoyé avec succès ! Vérifiez votre boîte de réception.';
          this.updateFormattedErrorMessage();
          this.showResendEmailOption = false;
        } else {
          this.errorMessage = response.message || '❌ Impossible de renvoyer l\'email. Veuillez réessayer plus tard.';
          this.updateFormattedErrorMessage();
        }
      },
      error: (error) => {
        this.isResendingEmail = false;
        this.errorMessage = '❌ Erreur lors du renvoi de l\'email. Veuillez réessayer.';
        this.updateFormattedErrorMessage();
        console.error('Erreur lors du renvoi de l\'email', error);
      }
    });
  }
}
